import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Heart, Bone, Brain, HeartHandshake, Weight, Scissors, Activity, Syringe, Ear, Droplets,
  ClipboardCheck, Shield, Stethoscope, Wind, ScanLine, HeartPulse, Dumbbell, Zap, ShieldOff,
  Flame, Leaf, Flower2, Sparkles, Dna, Smile, Eye, Waves, Pill, Baby, UserRound,
  Repeat, Ribbon, TestTubes, Sprout, Fingerprint, ShieldCheck, Droplet, Beaker, Radiation, Microscope,
  ChevronDown, Clock, Users, ArrowRight, MessageSquareHeart, Loader2, Search, X,
  Award, Globe, MapPin, CheckCircle2, Plus, Minus, Building2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Disclaimer, B9Disclaimer } from "@/components/Disclaimers";

const ICONS = {
  Heart, Bone, Brain, HeartHandshake, Weight, Scissors, Activity, Syringe, Ear, Droplets,
  ClipboardCheck, Shield, Stethoscope, Wind, ScanLine, HeartPulse, Dumbbell, Zap, ShieldOff,
  Flame, Leaf, Flower2, Sparkles, Dna, Smile, Eye, Waves, Pill, Baby, UserRound,
  Droplet, Beaker, Radiation, Microscope, Fingerprint, Repeat, Ribbon, TestTubes, Sprout,
};

// Specialty-specific, visually-distinct icons (overrides the backend's generic icon).
// No two specialties within the same category share an icon.
const SPECIALTY_ICON = {
  // Surgical
  "Cardiology & Cardiac Surgery": HeartPulse,
  "Orthopedics": Bone,
  "Neurosurgery": Brain,
  "Transplant Surgery": Repeat,
  "Bariatric & Metabolic Surgery": Dumbbell,
  "Cosmetic & Plastic Surgery": Sparkles,
  "Vascular Surgery": Activity,
  "General & Laparoscopic Surgery": Syringe,
  "ENT Surgery": Ear,
  "Urology": Droplets,
  "Pediatric Surgery & Pediatric Cardiology": Baby,
  // Clinical & Diagnostics
  "Comprehensive Health Checkups": ClipboardCheck,
  "Oncology & Cancer Care": Ribbon,
  "Gastroenterology": Stethoscope,
  "Pulmonology & Respiratory": Wind,
  "Diagnostic Imaging & Lab Work": TestTubes,
  "Nephrology & Dialysis": Droplet,
  "Hepatology & Liver Disease": Beaker,
  // Rehabilitation & Recovery
  "Cardiac Rehabilitation": Activity,
  "Orthopedic & Sports Rehabilitation": Dumbbell,
  "Neurological Rehabilitation": Zap,
  "Addiction Recovery & De-addiction": ShieldCheck,
  "Pain Management & Chronic Care": Flame,
  // Wellness & Alternative
  "B9Concept — Trauma-Based Healing": Sparkles,
  "Ayurveda & Traditional Medicine": Leaf,
  "Yoga & Meditation Therapy": Flower2,
  "Wellness & Preventive Retreats": Sprout,
  "Stem Cell & Regenerative Therapy": Dna,
  // Specialized Care
  "Dental & Maxillofacial": Smile,
  "Ophthalmology & Eye Care": Eye,
  "Dermatology & Hair Restoration": Fingerprint,
  "Endocrinology & Diabetes": Pill,
  "Rheumatology & Immunology": Microscope,
  "Radiation Oncology & Nuclear Medicine": Radiation,
  // Women & Children
  "Fertility & Reproductive Medicine": Baby,
  "Gynecology & Obstetrics": HeartHandshake,
};

const money = (n) => `$${Number(n).toLocaleString()}`;
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

const POPULAR_CHIPS = [
  "Knee Pain", "Heart Surgery", "IVF", "Hair Transplant",
  "Health Checkup", "Dental Implants", "Weight Loss", "Addiction Recovery",
];

const B9_NAME = "B9Concept — Trauma-Based Healing";

const B9_STEPS = [
  { n: 1, title: "Root Cause Analysis",
    body: "Using advanced precision diagnostics, B9Concept maps your body's energy field to pinpoint the exact disturbances and deep subconscious patterns driving your physical and emotional symptoms. You see the true source — in real time.",
    tags: ["Precision Diagnostics", "Real-Time Mapping", "Pattern Detection"] },
  { n: 2, title: "Deep Pattern Dissolution",
    body: "A proprietary recalibration process dissolves disruptive patterns at their origin. This releases stored stress, clears subconscious blocks, and restores your body's natural state of harmony. This isn't talk therapy — it's a profound biological reset.",
    tags: ["Core Recalibration", "Subconscious Release", "Harmonic Restoration"] },
  { n: 3, title: "Future Self Integration",
    body: "Once core issues are resolved, you receive powerful tools to strengthen resilience against future stressors and anchor your transformation — ensuring lasting freedom from chronic symptoms.",
    tags: ["Resilience Building", "Energy Fortification", "Lasting Freedom"] },
];

const B9_CONDITIONS = [
  { title: "Chronic Pain & Musculoskeletal", accent: "#EF4444", items: ["Fibromyalgia", "Chronic back/neck pain", "Migraines", "TMJ", "CRPS"] },
  { title: "Digestive & Gastrointestinal", accent: "#F59E0B", items: ["IBS", "IBD", "GERD", "Gastroparesis", "Food intolerances", "Chronic bloating"] },
  { title: "Cardiovascular & Heart", accent: "#EC4899", items: ["Hypertension", "Arrhythmias", "POTS", "Chronic chest pain"] },
  { title: "Skin & Dermatological", accent: "#8B5CF6", items: ["Psoriasis", "Eczema", "Chronic hives", "Rosacea", "Alopecia"] },
  { title: "Respiratory & Lung", accent: "#06B6D4", items: ["Asthma", "COPD", "Chronic cough", "Sleep apnea", "Chronic sinusitis"] },
  { title: "Reproductive & Hormonal", accent: "#D946EF", items: ["Endometriosis", "PCOS", "PMDD", "Infertility", "Erectile dysfunction"] },
  { title: "Neurological & Nervous System", accent: "#3B82F6", items: ["Peripheral neuropathy", "MS", "Vertigo", "ME/CFS", "Tinnitus"] },
  { title: "Autoimmune & Inflammatory", accent: "#10B981", items: ["Rheumatoid arthritis", "Lupus", "Hashimoto's", "Celiac disease"] },
  { title: "Mental Health & Psychiatric", accent: "#6366F1", items: ["Depression", "Anxiety", "PTSD", "OCD", "Panic disorder", "Insomnia"] },
  { title: "Autonomic Nervous System", accent: "#14B8A6", items: ["Dysautonomia", "Orthostatic intolerance", "Temperature dysregulation"] },
  { title: "Sleep & Circadian Rhythm", accent: "#0EA5E9", items: ["Chronic insomnia", "Night terrors", "Sleep paralysis", "Narcolepsy"] },
  { title: "Metabolic & Endocrine", accent: "#F97316", items: ["Type 2 diabetes", "Metabolic syndrome", "Hypothyroidism", "Insulin resistance"] },
];

const B9_COMPARISON = [
  { aspect: "Focus", traditional: "Symptom Management", b9: "Root Cause Resolution" },
  { aspect: "Method", traditional: "Medication & Talk Therapy", b9: "Bio-Energetic & Cognitive Reframing" },
  { aspect: "Duration", traditional: "Years / Lifelong", b9: "Same-Day Resolution" },
  { aspect: "Outcome", traditional: "Dependency & Recurring Issues", b9: "Permanent Freedom" },
  { aspect: "Side Effects", traditional: "Common (medication-related)", b9: "None (100% non-medicinal)" },
];

const CATEGORY_BANNERS = {
  "All": { src: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1200&h=300&fit=crop&q=80", alt: "Comprehensive medical services in India" },
  "Surgical": { src: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=300&fit=crop&q=80", alt: "Advanced surgical facilities" },
  "Clinical & Diagnostics": { src: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1200&h=300&fit=crop&q=80", alt: "Modern diagnostic laboratories" },
  "Rehabilitation & Recovery": { src: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1200&h=300&fit=crop&q=80", alt: "Rehabilitation and recovery services" },
  "Wellness & Alternative": { src: "https://images.unsplash.com/photo-1545389336-cf090694435e?w=1920&h=300&fit=crop&q=80", alt: "Wellness and alternative medicine" },
  "Specialized Care": { src: "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=1200&h=300&fit=crop&q=80", alt: "Specialized medical care" },
  "Women & Children": { src: "https://images.unsplash.com/photo-1531983412531-1f49a365ffed?w=1200&h=300&fit=crop&q=80", alt: "Women and children's healthcare" },
};

export default function ServicesExplorer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState("All");
  const [open, setOpen] = useState(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    setData(null);
    api.get("/v1/services")
      .then((r) => setData(r.data && Array.isArray(r.data.categories) ? r.data : { categories: [] }))
      .catch(() => { setError(true); setData({ categories: [] }); });
  };
  useEffect(load, []);

  // Deep-link: /services?focus=b9 opens the B9Concept featured specialty.
  useEffect(() => {
    if (data && searchParams.get("focus") === "b9") {
      setActiveCat("Wellness & Alternative");
      setOpen(B9_NAME);
      requestAnimationFrame(() => document.getElementById("service-card-b9concept-trauma-based-healing")?.scrollIntoView({ behavior: "smooth", block: "center" }));
    }
  }, [data, searchParams]);

  const findProviders = (specialty) => navigate(user?.role === "patient" ? `/book?specialty=${encodeURIComponent(specialty)}` : "/register");
  const talkAI = (specialty) => navigate(user?.role === "patient" ? `/assistant?specialty=${encodeURIComponent(specialty)}` : "/register");

  const categories = data?.categories || [];
  const tabs = ["All", ...categories.map((c) => c.name)];

  const matchesQuery = (s) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = [s.name, ...(s.procedures || []).map((p) => p.name), ...(s.keywords || [])].join(" ").toLowerCase();
    return hay.includes(q);
  };

  // Categories filtered by the active tab + live search query.
  const filteredCategories = useMemo(() => (
    categories
      .filter((cat) => activeCat === "All" || activeCat === cat.name)
      .map((cat) => ({ ...cat, specialties: cat.specialties.filter(matchesQuery) }))
      .filter((cat) => cat.specialties.length > 0)
  ), [categories, activeCat, query]);

  const totalMatches = filteredCategories.reduce((n, c) => n + c.specialties.length, 0);
  const totalSpecialties = categories.reduce((n, c) => n + c.specialties.length, 0);

  return (
    <div className="bg-[#F7F9FC] min-h-screen">
      <section className="mv-gradient-hero text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold">Explore Medical Services</h1>
          <p className="mt-3 text-white/85 max-w-2xl text-lg">{totalSpecialties || 36} specialties across {categories.length || 6} categories — compare savings and find accredited providers for your treatment.</p>
        </div>
      </section>

      {/* Search + popular chips — glass card on a tinted band */}
      <section className="section-tinted">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <div className="card-modern tricolor-top p-6 sm:p-8">
            <div className="relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(null); }}
                placeholder="Search specialties — e.g. 'knee pain', 'heart surgery', 'IVF'..."
                className="h-14 rounded-2xl bg-white text-[#101828] shadow-sm border border-[#E4E7EC] pl-12 pr-11 text-base placeholder:italic focus-visible:ring-2 focus-visible:ring-[#2A9D8F] focus-visible:border-[#2A9D8F]"
                data-testid="services-search-input"
              />
              {query && (
                <button onClick={() => setQuery("")} data-testid="services-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center h-7 w-7 rounded-full text-[#667085] hover:bg-[#F2F4F7] transition-colors">
                  <X size={18} />
                </button>
              )}
            </div>

            <div className="chips-fade mt-5">
              <div className="flex gap-2 overflow-x-auto no-scrollbar pr-10" data-testid="popular-chips">
                {POPULAR_CHIPS.map((chip) => {
                  const active = query.trim().toLowerCase() === chip.toLowerCase();
                  return (
                    <button key={chip} data-testid={`chip-${slug(chip)}`}
                      onClick={() => { setQuery(active ? "" : chip); setOpen(null); }}
                      className={`tag-pill shrink-0 !px-4 !py-1.5 !text-sm transition-all duration-200 cursor-pointer ${
                        active ? "!bg-gradient-to-r !from-[#0D5C75] !to-[#2A9D8F] !text-white !border-transparent" : "hover:!bg-[rgba(42,157,143,0.2)]"
                      }`}>
                      {chip}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex gap-2 overflow-x-auto py-3 no-scrollbar" data-testid="category-tabs">
            {tabs.map((t) => {
              const cat = categories.find((c) => c.name === t);
              const isActive = activeCat === t;
              return (
                <button key={t} onClick={() => { setActiveCat(t); setOpen(null); }}
                  data-testid={`cat-tab-${slug(t)}`}
                  className={`shrink-0 rounded-full px-5 py-2 text-sm transition-all duration-300 border ${
                    isActive
                      ? "text-white border-transparent font-semibold shadow-[0_4px_12px_rgba(13,92,117,0.25)]"
                      : "bg-white text-[#6B7280] border-[#E4E7EC] font-medium hover:bg-[rgba(42,157,143,0.08)] hover:text-[#0D5C75] hover:border-[rgba(42,157,143,0.3)]"
                  }`}
                  style={isActive ? { background: "linear-gradient(135deg, #0D5C75, #2A9D8F)" } : undefined}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-12">
        {!data ? (
          <div className="py-20 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>
        ) : error ? (
          <div className="py-16 text-center" data-testid="services-error">
            <Search size={40} className="mx-auto text-[#98A2B3]" />
            <h3 className="font-heading font-bold text-lg text-[#101828] mt-4">Couldn't load services</h3>
            <p className="text-muted-foreground text-sm mt-1">Please check your connection and try again.</p>
            <Button className="mt-5 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" onClick={load} data-testid="services-retry-btn">Retry</Button>
          </div>
        ) : totalMatches === 0 ? (
          <div className="py-16 text-center" data-testid="services-no-match">
            <Search size={40} className="mx-auto text-[#98A2B3]" />
            <h3 className="font-heading font-bold text-lg text-[#101828] mt-4">No matching services found</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">Try different keywords or talk to our AI assistant for personalized guidance.</p>
            <Button className="mt-5 rounded-full bg-[#8B5CF6] text-white hover:bg-[#7c4ddb] hover:text-white"
              onClick={() => navigate(user?.role === "patient" ? "/assistant" : "/register")} data-testid="no-match-ai-btn">
              <MessageSquareHeart size={16} className="mr-1.5" /> Talk to AI Assistant
            </Button>
          </div>
        ) : (
          <>
            {activeCat === "All" ? (
              filteredCategories.map((cat) => (
                <div key={cat.name} className="mb-12" data-testid={`category-section-${slug(cat.name)}`}>
                  <div className="mt-2 mb-6 pb-4 border-b border-black/5">
                    <span className="tricolor-short mb-3 block" />
                    <div className="flex items-center gap-3">
                      <h2 className="font-heading font-bold text-2xl text-[#0D5C75] tracking-tight">{cat.name}</h2>
                      <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-[13px] font-semibold text-[#0D5C75] bg-[rgba(42,157,143,0.12)]">{cat.specialties.length}</span>
                    </div>
                  </div>
                  <CardGrid specialties={cat.specialties} open={open} setOpen={setOpen} findProviders={findProviders} talkAI={talkAI} />
                </div>
              ))
            ) : (
              <CardGrid specialties={filteredCategories[0]?.specialties || []} open={open} setOpen={setOpen} findProviders={findProviders} talkAI={talkAI} />
            )}
          </>
        )}
        <Disclaimer testid="services-pricing-disclaimer" className="mt-8 max-w-3xl">
          Prices shown are estimates based on published hospital rates and may vary based on individual case complexity, diagnostic findings, and treatment plan. Final pricing is confirmed by your chosen provider after medical evaluation.
        </Disclaimer>
      </div>
    </div>
  );
}

function CardGrid({ specialties, open, setOpen, findProviders, talkAI }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
      {specialties.map((s) => {
        const Icon = SPECIALTY_ICON[s.name] || ICONS[s.icon] || Stethoscope;
        const isOpen = open === s.name;
        const sl = slug(s.name);
        if (s.featured) {
          return <FeaturedB9Card key={s.name} s={s} sl={sl} isOpen={isOpen} setOpen={setOpen} findProviders={findProviders} talkAI={talkAI} />;
        }
        return (
          <motion.div key={s.name} layout id={`service-card-${sl}`}
            className="group card-modern border-l-4 hover:border-l-[6px] overflow-hidden relative flex flex-col transition-[border-left-width] duration-300"
            style={{ borderLeftColor: s.accent, "--cat": s.accent, "--cat-tint": `${s.accent}1A` }} data-testid={`service-card-${sl}`}>
            <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${s.accent}, transparent)` }} />
            <div className="p-7 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-3">
                <span className="svc-icon grid place-items-center h-16 w-16 rounded-full shrink-0"><Icon size={28} strokeWidth={2} /></span>
                <Badge className="bg-gradient-to-r from-[#D97706] to-[#F97316] text-white border-0 shadow-sm">Save {s.savings_range}</Badge>
              </div>
              <h3 className="font-heading font-bold text-[1.375rem] leading-tight text-[#0D5C75] mt-4">{s.name}</h3>
              <p className="mt-2 text-sm text-[#6B7280] leading-relaxed line-clamp-2">{s.description}</p>

              {s.procedures?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid={`procedure-pills-${sl}`}>
                  {s.procedures.slice(0, 4).map((p) => (
                    <span key={p.name} className="tag-pill">{p.name}</span>
                  ))}
                  {s.procedures.length > 4 && (
                    <span className="tag-pill !bg-transparent !text-[#6B7280] !border-[#E4E7EC]">+{s.procedures.length - 4}</span>
                  )}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-black/5 flex items-center gap-4 flex-wrap text-[0.8125rem] font-medium uppercase tracking-[0.06em] text-[#9CA3AF]">
                {s.provider_count > 0 ? (
                  <span className="flex items-center gap-1.5"><Building2 size={14} className="text-[#2A9D8F]" /> {s.provider_count} provider{s.provider_count === 1 ? "" : "s"}</span>
                ) : (
                  <Badge className="bg-[#FEF3C7] text-[#92400E] border-0 normal-case" data-testid={`coming-soon-${sl}`}>Coming Soon</Badge>
                )}
                <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#2A9D8F]" /> {s.recovery}</span>
              </div>

              <button onClick={() => setOpen(isOpen ? null : s.name)}
                className="mt-auto pt-5 w-full"
                data-testid={`explore-${sl}`}>
                <span className="w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-[0.9375rem] font-semibold text-white bg-gradient-to-br from-[#0D5C75] to-[#2A9D8F] shadow-[0_4px_14px_rgba(13,92,117,0.28)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(13,92,117,0.38)] transition-all duration-300">
                  {isOpen ? "Hide details" : "Explore Treatments"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            </div>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 30 }} className="overflow-hidden">
                  <div className="px-6 pb-6">
                    <div className="rounded-xl bg-[#F7F9FC] p-4">
                      <div className="text-xs font-semibold text-[#101828] uppercase tracking-wide mb-2">Common procedures</div>
                      <ul className="space-y-1.5">
                        {s.procedures.map((p) => (
                          <li key={p.name} className="flex justify-between items-baseline gap-3 text-sm">
                            <span className="text-muted-foreground">{p.name}</span>
                            <span className="whitespace-nowrap">
                              <span className="font-semibold text-[#0D5C75]">{money(p.india_price)}</span>
                              {p.usa_price ? <span className="text-xs text-muted-foreground line-through ml-1.5">{money(p.usa_price)}</span> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                      <Button className="flex-1 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white"
                        onClick={() => findProviders(s.name)} data-testid={`find-providers-${sl}`}>
                        Find Providers <ArrowRight size={15} className="ml-1" />
                      </Button>
                      <Button variant="outline" className="flex-1 rounded-full border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/5"
                        onClick={() => talkAI(s.name)} data-testid={`talk-ai-${sl}`}>
                        <MessageSquareHeart size={15} className="mr-1" /> Talk to AI
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}

function FeaturedB9Card({ s, sl, isOpen, setOpen, findProviders, talkAI }) {
  const Icon = ICONS[s.icon] || Sparkles;
  const [expandedCond, setExpandedCond] = useState(null);
  return (
    <motion.div key={s.name} layout id={`service-card-${sl}`}
      className="md:col-span-2 lg:col-span-3 bg-gradient-to-br from-[#FFFBEB] to-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-[#D97706]/50 overflow-hidden relative"
      data-testid={`service-card-${sl}`}>
      <div className="absolute top-0 right-0">
        <div className="flex items-center gap-1.5 bg-[#D97706] text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl" data-testid="b9-featured-badge">
          <Award size={13} /> FEATURED PARTNER
        </div>
      </div>
      <div className="p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <span className="grid place-items-center h-14 w-14 rounded-xl text-white shrink-0 bg-gradient-to-br from-[#D97706] to-[#B45309]"><Icon size={28} /></span>
          <div className="flex-1 min-w-0 pr-24 sm:pr-28">
            <h3 className="font-heading font-extrabold text-xl sm:text-2xl text-[#101828]">{s.name}</h3>
            <p className="mt-1 text-sm font-medium text-[#B45309] italic">"{s.tagline}"</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-[#475467] leading-relaxed max-w-3xl">{s.description}</p>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Users, label: "10,000+ lives transformed worldwide" },
            { icon: Clock, label: "Same-day to short-term programs" },
            { icon: CheckCircle2, label: "100% non-medicinal, non-pharmaceutical" },
            { icon: MapPin, label: "Nashik & Noida, India" },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-white border border-[#FDE68A] p-3">
              <f.icon size={16} className="text-[#D97706] mt-0.5 shrink-0" />
              <span className="text-xs text-[#475467] leading-snug">{f.label}</span>
            </div>
          ))}
        </div>
        <button onClick={() => setOpen(isOpen ? null : s.name)}
          className="mt-5 w-full flex items-center justify-center gap-1.5 rounded-lg bg-[#D97706] py-2.5 text-sm font-semibold text-white hover:bg-[#B45309] transition-colors duration-200"
          data-testid={`explore-${sl}`}>
          {isOpen ? "Hide details" : "How B9Concept Works"} <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }} className="overflow-hidden" data-testid="b9-detail-view">
            <div className="px-6 sm:px-8 pb-8 space-y-10">
              {/* 3-step process */}
              <div>
                <h4 className="font-heading font-bold text-lg text-[#101828] text-center">The B9 Transformation Journey — Find the Root, Not the Symptom</h4>
                <div className="mt-6 grid md:grid-cols-3 gap-6 relative" data-testid="b9-steps">
                  {B9_STEPS.map((st) => (
                    <div key={st.n} className="relative bg-white rounded-xl border border-[#FDE68A] p-5">
                      <div className="grid place-items-center h-10 w-10 rounded-full bg-[#D97706] text-white font-bold mb-3">{st.n}</div>
                      <div className="font-heading font-bold text-[#101828]">Step {st.n} — {st.title}</div>
                      <p className="mt-2 text-xs text-[#475467] leading-relaxed">{st.body}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {st.tags.map((t) => <span key={t} className="text-[10px] rounded-full bg-[#FFFBEB] border border-[#FDE68A] text-[#B45309] px-2 py-0.5">{t}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conditions grid */}
              <div>
                <h4 className="font-heading font-bold text-lg text-[#101828] text-center">137+ Chronic Conditions Addressed</h4>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="b9-conditions-grid">
                  {B9_CONDITIONS.map((c) => {
                    const exp = expandedCond === c.title;
                    return (
                      <button key={c.title} onClick={() => setExpandedCond(exp ? null : c.title)}
                        className="text-left rounded-xl bg-white border-t-4 border border-[#E4E7EC] p-4 hover:shadow-md transition-shadow duration-200"
                        style={{ borderTopColor: c.accent }} data-testid={`b9-condition-${slug(c.title)}`}>
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-heading font-semibold text-sm text-[#101828] leading-snug">{c.title}</span>
                          {exp ? <Minus size={14} className="text-[#98A2B3] shrink-0 mt-1" /> : <Plus size={14} className="text-[#98A2B3] shrink-0 mt-1" />}
                        </div>
                        <div className="mt-2 text-xs text-[#667085] leading-relaxed">
                          {(exp ? c.items : c.items.slice(0, 3)).join(" • ")}{!exp && c.items.length > 3 ? " …" : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Comparison table */}
              <div>
                <h4 className="font-heading font-bold text-lg text-[#101828] text-center">B9Concept vs Traditional Medicine</h4>
                <div className="mt-6 overflow-x-auto rounded-2xl border border-[#E4E7EC] bg-white shadow-sm">
                  <table className="w-full text-sm" data-testid="b9-comparison-table">
                    <thead>
                      <tr className="text-left">
                        <th className="p-4 font-heading bg-[#F2F4F7] text-[#101828]">Aspect</th>
                        <th className="p-4 font-heading bg-[#F2F4F7] text-[#475467]">Traditional Approach</th>
                        <th className="p-4 font-heading bg-[#D97706] text-white">B9Concept</th>
                      </tr>
                    </thead>
                    <tbody>
                      {B9_COMPARISON.map((r, i) => (
                        <tr key={r.aspect} className={i % 2 ? "bg-[#F7F9FC]" : "bg-white"}>
                          <td className="p-4 font-medium text-[#101828]">{r.aspect}</td>
                          <td className="p-4 text-muted-foreground">{r.traditional}</td>
                          <td className="p-4 bg-[#FFFBEB] font-semibold text-[#B45309]">{r.b9}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
                <Button className="flex-1 rounded-full bg-[#D97706] text-white hover:bg-[#B45309] hover:text-white"
                  onClick={() => findProviders(s.name)} data-testid={`find-providers-${sl}`}>
                  Find Providers <ArrowRight size={15} className="ml-1" />
                </Button>
                <Button variant="outline" className="flex-1 rounded-full border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/5"
                  onClick={() => talkAI(s.name)} data-testid={`talk-ai-${sl}`}>
                  <MessageSquareHeart size={15} className="mr-1" /> Talk to AI
                </Button>
              </div>

              <B9Disclaimer testid="b9-specialty-disclaimer" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
